import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtTime } from "@/lib/caesar";
import { Loading } from "@/components/common";
import { ListCard, MutedEmpty, ActionBtn } from "@/components/widgets";
import { toast } from "sonner";
import { Check, Phone, MessageSquare, Pencil, ArrowUpRight, Flag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Labeled } from "@/components/common";

const REASONS = ["Decision", "Customer issue", "Payment", "Opportunity", "Relationship", "Emergency", "Approval"];

export function EscalateButton({ title, onDone, testId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("Decision");
  const [note, setNote] = useState("");
  const submit = async () => {
    await api.post("/escalations", { title, reason, note, status: "open" });
    toast.success("Escalated to Toriano");
    setOpen(false); setNote("");
    onDone && onDone();
  };
  return (
    <>
      <button data-testid={testId} onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-sred/40 text-xs font-semibold text-sred hover:bg-sred/10 transition-colors">
        <Flag size={13} /> Needs Toriano
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-edge">
          <DialogHeader><DialogTitle className="font-display text-xl">Escalate to Toriano</DialogTitle></DialogHeader>
          <p className="text-sm text-[#A3A3A3]">{title}</p>
          <Labeled label="Reason">
            <div className="flex flex-wrap gap-2">
              {REASONS.map((r) => (
                <button key={r} onClick={() => setReason(r)} className={"px-3 py-1.5 rounded-full text-xs font-semibold border " + (reason === r ? "bg-sred text-white border-sred" : "border-edge text-[#A3A3A3]")}>{r}</button>
              ))}
            </div>
          </Labeled>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note (optional)" className="bg-ink border-edge" />
          <button data-testid="escalate-submit" onClick={submit} className="h-11 rounded-lg bg-sred text-white font-bold">Send to Commander</button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function VivianCommand() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/dashboard/vivian"); setData(data); }, []);
  useEffect(() => { load(); }, [load]);
  const complete = async (id) => { await api.put(`/tasks/${id}`, { status: "done" }); toast.success("Done"); load(); };

  if (!data) return <Loading label="Loading your command…" />;
  const first = data.do_this_now;

  return (
    <div className="space-y-8">
      <div>
        <p className="label-caps text-gold">Vivian Command</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Do this next.</h1>
      </div>

      {/* DO THIS NOW */}
      <div className="bg-surface border border-gold/40 rounded-xl p-5 sm:p-6" data-testid="do-this-now">
        <span className="label-caps text-gold">Do This Now</span>
        {first ? (
          <>
            <p className="mt-3 text-xl sm:text-2xl font-display font-semibold leading-snug">{first.title}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <ActionBtn testId="do-now-complete" onClick={() => complete(first.id)} icon={Check} label="Complete" primary />
              <EscalateButton title={first.title} onDone={load} testId="do-now-escalate" />
            </div>
          </>
        ) : <p className="mt-3 text-[#A3A3A3]">No assigned action right now. Great work — capture the next thing.</p>}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ListCard title="Today's Assignments" count={data.assignments.length} testId="vivian-assignments">
          {data.assignments.length === 0 ? <MutedEmpty>No assignments.</MutedEmpty> :
            data.assignments.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg gap-2">
                <div className="min-w-0"><p className="text-sm truncate">{t.title}</p>{t.deadline && <span className="label-caps text-[0.55rem] text-samber">{fmtDate(t.deadline)}</span>}</div>
                <button data-testid={`vivian-complete-${t.id}`} onClick={() => complete(t.id)} className="text-sgreen shrink-0"><Check size={16} /></button>
              </div>
            ))}
        </ListCard>

        <ListCard title="People to Contact" count={data.people_to_contact.length} testId="vivian-contact">
          {data.people_to_contact.length === 0 ? <MutedEmpty>No one to contact.</MutedEmpty> :
            data.people_to_contact.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg gap-2">
                <p className="text-sm truncate">{p.name}</p>
                <div className="flex gap-1 shrink-0">
                  {p.phone && <a href={`tel:${p.phone}`} className="text-gold p-1"><Phone size={15} /></a>}
                  <button onClick={() => navigate("/people")} className="text-[#A3A3A3] p-1"><ArrowUpRight size={15} /></button>
                </div>
              </div>
            ))}
        </ListCard>

        <ListCard title="Hot Leads" count={data.hot_leads.length} testId="vivian-hotleads">
          {data.hot_leads.length === 0 ? <MutedEmpty>No hot leads.</MutedEmpty> :
            data.hot_leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/people")}>
                <div className="min-w-0"><p className="text-sm truncate">{l.name}</p><span className="label-caps text-[0.55rem] text-sred">{l.stage}</span></div>
                {l.estimated_value ? <span className="font-num text-sm text-sgreen shrink-0 ml-2">{fmtMoney(l.estimated_value)}</span> : null}
              </div>
            ))}
        </ListCard>

        <ListCard title="Customers Needing Updates" count={data.customers_needing_updates.length} testId="vivian-customers">
          {data.customers_needing_updates.length === 0 ? <MutedEmpty>All customers up to date.</MutedEmpty> :
            data.customers_needing_updates.map((c) => (<div key={c.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/people")}><p className="text-sm">{c.name}</p><span className="label-caps text-[0.55rem]">{c.next_action}</span></div>))}
        </ListCard>

        <ListCard title="Money to Follow Up" count={data.money_followup.length} testId="vivian-money">
          {data.money_followup.length === 0 ? <MutedEmpty>No money to chase.</MutedEmpty> :
            data.money_followup.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/money")}>
                <div className="min-w-0"><p className="text-sm truncate">{m.party}</p><span className="label-caps text-[0.55rem]">{m.status}</span></div>
                <span className="font-num text-sm text-samber shrink-0 ml-2">{fmtMoney(m.amount)}</span>
              </div>
            ))}
        </ListCard>

        <ListCard title="Project Updates Needed" count={data.project_updates.length} testId="vivian-projects">
          {data.project_updates.length === 0 ? <MutedEmpty>All projects have a next move.</MutedEmpty> :
            data.project_updates.map((p) => (<div key={p.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/projects")}><p className="text-sm">{p.name}</p><span className="label-caps text-[0.55rem] text-samber">no next action</span></div>))}
        </ListCard>

        <ListCard title="Upcoming Calendar" count={data.upcoming.length} testId="vivian-calendar">
          {data.upcoming.length === 0 ? <MutedEmpty>No upcoming events.</MutedEmpty> :
            data.upcoming.map((e) => (<div key={e.id} className="flex items-center gap-3 px-3 py-2.5"><span className="font-num text-xs text-gold w-24 shrink-0">{fmtDate(e.time_start)} {fmtTime(e.time_start)}</span><p className="text-sm truncate">{e.title}</p></div>))}
        </ListCard>

        <ListCard title="Deadlines" count={data.deadlines.length} testId="vivian-deadlines">
          {data.deadlines.length === 0 ? <MutedEmpty>No near deadlines.</MutedEmpty> :
            data.deadlines.map((t) => (<div key={t.id} className="px-3 py-2.5"><p className="text-sm">{t.title}</p><span className="label-caps text-[0.55rem] text-samber">{fmtDate(t.deadline)}</span></div>))}
        </ListCard>

        <ListCard title="Needs Toriano" count={data.needs_toriano.length} testId="vivian-escalations">
          {data.needs_toriano.length === 0 ? <MutedEmpty>Nothing escalated.</MutedEmpty> :
            data.needs_toriano.map((e) => (<div key={e.id} className="px-3 py-2.5"><p className="text-sm">{e.title}</p><span className="label-caps text-[0.55rem] text-sred">{e.reason} · {e.status}</span></div>))}
        </ListCard>
      </div>
    </div>
  );
}
